import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  DataTable,
  HairlineCard,
  Link,
  RowActions,
  Stack,
  StatusChip,
  TagChip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  palette,
  useConfirm,
  useRowActionsColumn,
} from '@munifocus/ui';
import type { DispatchFile, DispatchReason, DispatchSuggestion } from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { PageIntro } from '../../components/PageIntro';
import { useMutationToast } from '../../lib/mutation-toast';
import { useAssignFile, useDispatchBoard, useDispatchSuggestions } from '../../lib/operations-api';
import { formatDistance } from '../../lib/operations-format';
import { OpsMap } from './OpsMap';
import { LoadError, Loading, OpsScopeNotice, useOpsMunicipality } from './shared';

const PRIORITY_RANK = { urgent: 0, elevated: 1, routine: 2 } as const;

/** Unassigned first, then emergencies and urgent files, then the oldest. */
export function sortForDispatch(files: DispatchFile[]): DispatchFile[] {
  return [...files].sort(
    (a, b) =>
      Number(!!a.assignedOfficerId) - Number(!!b.assignedOfficerId) ||
      Number(b.emergency) - Number(a.emergency) ||
      PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
      a.createdAt.localeCompare(b.createdAt),
  );
}

/**
 * The supervisor's dispatch board.
 *
 * Open files at the active town, unassigned first; choosing one ranks the
 * officers who could take it — authority, on duty, distance, caseload — and
 * assigning goes through the same checks as the file form. Distances appear
 * only for a supervisor who may see the team map; a contractor's supervisor
 * sees only its own officers, and files that are free or already theirs.
 */
export function Dispatch() {
  const { t } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const { municipality, reason } = useOpsMunicipality((m) => m.canDispatch);
  const lgId = municipality?.localGovernmentId;
  const board = useDispatchBoard(lgId);
  const [filter, setFilter] = useState<'unassigned' | 'all'>('unassigned');
  const [selected, setSelected] = useState<string | null>(null);

  const files = useMemo(() => {
    const all = sortForDispatch(board.data?.files ?? []);
    return filter === 'unassigned' ? all.filter((f) => !f.assignedOfficerId) : all;
  }, [board.data, filter]);
  const file = board.data?.files.find((f) => f.fileId === selected) ?? null;

  const pins = files
    .filter((f) => f.latitude !== null && f.longitude !== null)
    .map((f) => ({
      id: f.fileId,
      lat: f.latitude as number,
      lng: f.longitude as number,
      title: `${f.reference} — ${f.address}`,
      emphasis: f.fileId === selected || !selected,
    }));

  return (
    <AppShell title={t('gov.ops.dispatch.title')}>
      <PageIntro>{t('gov.ops.dispatch.intro')}</PageIntro>
      {reason && <OpsScopeNotice reason={reason} />}
      {lgId && board.isLoading && <Loading />}
      {lgId && board.isError && (
        <LoadError onRetry={() => void board.refetch()}>
          {t('gov.ops.dispatch.loadError')}
        </LoadError>
      )}
      {lgId && board.data && (
        <Stack spacing={2}>
          {board.data.restrictedToCompanyIds && (
            <Alert severity="info">{t('gov.ops.dispatch.contractorView')}</Alert>
          )}
          {pins.length > 0 && <OpsMap pins={pins} onSelect={setSelected} height={280} />}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(0, 2fr)' },
              alignItems: 'start',
            }}
          >
            <DataTable
              label={t('gov.ops.dispatch.filesTitle')}
              rows={files}
              getRowKey={(f) => f.fileId}
              emptyLabel={
                filter === 'unassigned'
                  ? t('gov.ops.dispatch.noUnassigned')
                  : t('gov.ops.dispatch.noFiles')
              }
              pageSize={25}
              onRowClick={(f) => setSelected(f.fileId)}
              rowSx={(f) =>
                f.fileId === selected ? { backgroundColor: palette.brand.accentTint } : undefined
              }
              search={{
                placeholder: t('gov.ops.dispatch.search'),
                of: (f) => `${f.reference} ${f.address} ${f.assignedOfficerName ?? ''}`,
              }}
              filters={
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={filter}
                  onChange={(_, v) => v && setFilter(v)}
                  aria-label={t('gov.ops.dispatch.filter')}
                >
                  <ToggleButton value="unassigned">{t('gov.ops.dispatch.unassigned')}</ToggleButton>
                  <ToggleButton value="all">{t('gov.ops.dispatch.allOpen')}</ToggleButton>
                </ToggleButtonGroup>
              }
              columns={[
                {
                  key: 'reference',
                  label: t('gov.ops.dispatch.file'),
                  primary: true,
                  sortValue: (f) => f.reference,
                  cell: (f) => (
                    <Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {f.reference}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {f.address}
                      </Typography>
                    </Stack>
                  ),
                },
                {
                  key: 'priority',
                  label: t('gov.ops.dispatch.priority'),
                  sortValue: (f) => (f.emergency ? -1 : PRIORITY_RANK[f.priority]),
                  cell: (f) =>
                    f.emergency ? (
                      <StatusChip status="emergency" label={t('gov.ops.dispatch.emergency')} />
                    ) : (
                      <TagChip label={t(`gov.priority.${f.priority}`)} />
                    ),
                },
                {
                  key: 'assignee',
                  label: t('gov.ops.dispatch.assignee'),
                  sortValue: (f) => f.assignedOfficerName ?? '',
                  cell: (f) =>
                    f.assignedOfficerName ?? (
                      <Typography variant="body2" color="text.secondary">
                        {t('gov.ops.dispatch.nobody')}
                      </Typography>
                    ),
                },
                {
                  ...actionsColumn,
                  cell: (f) => <RowActions onView={() => setSelected(f.fileId)} />,
                },
              ]}
            />
            <SuggestionPanel
              lgId={lgId}
              file={file}
              positionsVisible={board.data.positionsVisible}
            />
          </Box>
        </Stack>
      )}
    </AppShell>
  );
}

function SuggestionPanel({
  lgId,
  file,
  positionsVisible,
}: {
  lgId: string;
  file: DispatchFile | null;
  positionsVisible: boolean;
}) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const toast = useMutationToast();
  const suggestions = useDispatchSuggestions(lgId, file?.fileId ?? null);
  const assign = useAssignFile(lgId);

  if (!file) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.ops.dispatch.pickFile')}
        </Typography>
      </HairlineCard>
    );
  }

  const onAssign = async (s: DispatchSuggestion) => {
    const reassigning = !!file.assignedOfficerId;
    const ok = await confirm({
      title: t(reassigning ? 'gov.ops.dispatch.confirmReassign' : 'gov.ops.dispatch.confirmAssign'),
      body: t('gov.ops.dispatch.confirmBody', {
        file: file.reference,
        officer: s.officer.displayName,
      }),
      confirmLabel: t(reassigning ? 'gov.ops.dispatch.reassign' : 'gov.ops.dispatch.assign'),
      cancelLabel: t('common.actions.cancel'),
    });
    if (!ok) return;
    try {
      await assign.mutateAsync({ fileId: file.fileId, officerId: s.officer.userId });
      toast.success('gov.ops.dispatch.assigned', {
        file: file.reference,
        officer: s.officer.displayName,
      });
    } catch (err) {
      toast.error(err, 'gov.ops.dispatch.assignFailed');
    }
  };

  return (
    <HairlineCard sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" component="h2">
        {t('gov.ops.dispatch.suggestionsFor', { file: file.reference })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        <Link component={RouterLink} to={`/files/${file.fileId}`}>
          {file.address}
        </Link>
      </Typography>
      {!positionsVisible && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
          {t('gov.ops.dispatch.noPositions')}
        </Typography>
      )}
      {suggestions.isLoading && <Loading />}
      {suggestions.isError && (
        <LoadError onRetry={() => void suggestions.refetch()}>
          {t('gov.ops.dispatch.suggestionsError')}
        </LoadError>
      )}
      {suggestions.data?.length === 0 && (
        <Alert severity="info">{t('gov.ops.dispatch.noOfficers')}</Alert>
      )}
      <Stack spacing={1.5} component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {(suggestions.data ?? []).map((s, i) => (
          <Box
            component="li"
            key={s.officer.userId}
            sx={{
              p: 1.5,
              border: `1px solid ${palette.border.line}`,
              borderRadius: 1,
              backgroundColor: i === 0 ? palette.brand.accentTint : 'transparent',
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {s.officer.displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="p">
                  {[
                    s.officer.companyName ?? t('gov.ops.dispatch.townOfficer'),
                    t('gov.ops.dispatch.openFiles', { count: s.officer.openFiles }),
                    s.officer.onDuty ? t('gov.ops.dispatch.onDuty') : t('gov.ops.dispatch.offDuty'),
                    s.officer.distanceMeters !== null
                      ? t('gov.ops.dispatch.away', {
                          distance: formatDistance(s.officer.distanceMeters, i18n.language),
                        })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                  {s.reasons.map((r: DispatchReason) => (
                    <TagChip key={r} label={t(`gov.ops.dispatch.reasons.${r}`)} />
                  ))}
                  {!s.hasAuthority && (
                    <StatusChip status="warning" label={t('gov.ops.dispatch.noAuthority')} />
                  )}
                </Stack>
              </Box>
              {file.assignedOfficerId !== s.officer.userId && (
                <Button
                  size="small"
                  variant={i === 0 ? 'contained' : 'outlined'}
                  disabled={assign.isPending}
                  onClick={() => void onAssign(s)}
                  aria-label={t('gov.ops.dispatch.assignTo', { officer: s.officer.displayName })}
                >
                  {t(
                    file.assignedOfficerId
                      ? 'gov.ops.dispatch.reassign'
                      : 'gov.ops.dispatch.assign',
                  )}
                </Button>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </HairlineCard>
  );
}
